import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useErnie from 'shared/hooks/use-ernie';
import useStores from 'shared/hooks/use-stores';
import useTranslation from 'hooks/use-translation';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useColors from 'src/hooks/use-colors';
import useUI from 'src/hooks/use-ui';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { tracker } from 'src/utils/analytics';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';

import { InfoIconAlt } from 'assets/info-icon-alt';
import { BankIcon } from 'assets/bank-icon';
import { Typography } from 'src/typography';
import { editBankAccount, PBB_NAME, relinkBankAccount } from 'src/payments/constants';

import { useDutchiePay } from 'src/payments/hooks';
import {
  BankCardContainer,
  BankDetail,
  BankInfo,
  BankInfoInner,
  BankLogo,
  BankName,
  BankTextWrapper,
  BankWrapper,
  EditButton,
  ErrorWrapper,
  RelinkButton,
  StyledTitle,
} from './existing-user.styles';
import { AddToWallet } from '../loyalty-pass';

type RelinkBankButtonProps = {
  onClick: () => void;
  label: string;
};

const RelinkBankButton = ({ onClick, label }: RelinkBankButtonProps): JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.1, freezeOnceVisible: true });

  useEffect(() => {
    if (entry?.isIntersecting) {
      tracker.dutchiePayRelinkButtonViewed({});
    }
  }, [entry?.isIntersecting]);

  return (
    <RelinkButton ref={ref} type='button' onClick={onClick}>
      {label}
    </RelinkButton>
  );
};

export function ExistingUser(): JSX.Element {
  const flags = useFlags();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { activeBankAccount: bankAccountData, whiteLabelConfig, needsRelinkBankAccount } = useDutchiePay();

  const { Cart } = useStores();
  const { isEmbedded } = useUI();
  const showErnie = useErnie();
  const { t } = useTranslation();
  const colors = useColors();
  const { dispensary } = useDispensary();
  const cname = Cart?.dispensary?.cName ?? dispensary?.cName;

  const [relinking, setRelinking] = useState(false);
  const step = needsRelinkBankAccount ? relinkBankAccount : editBankAccount;
  const { openDutchiePay } = useOpenDutchiePay({
    cname,
    step,
  });

  const financialInstitutionLogo =
    bankAccountData?.financialInstitutionLogo && `data:image/png;base64, ${bankAccountData.financialInstitutionLogo}`;

  const handleEditClick = (): void => {
    openDutchiePay(editBankAccount);
    DutchiePayEnrollment.editBankAccount();
  };

  const handleRelinkClick = (): void => {
    tracker.dutchiePayRelinkButtonClicked({});
    openDutchiePay(relinkBankAccount);
    DutchiePayEnrollment.reconnectBankAccount();
    setRelinking(true);
  };

  const handleRelinkSucccess = useCallback(() => {
    showErnie(`Your ${PBB_NAME} account has been relinked successfully!`, 'success');
    setRelinking(false);
  }, [showErnie]);

  useEffect(() => {
    if (relinking && bankAccountData && !needsRelinkBankAccount) {
      handleRelinkSucccess();
    }
  }, [relinking, bankAccountData, needsRelinkBankAccount, handleRelinkSucccess]);

  const pbbName = whiteLabelConfig.useWhiteLabel ? whiteLabelConfig.brandedDutchiePayName : PBB_NAME;

  const isShowLoyaltyPassFlagEnabled = flags['ecomm.loyalty-pass-ctas.rollout'] ?? false;

  return (
    <>
      <Typography.Body size='large'>
        {isEmbedded
          ? `Once you set up ${String(pbbName)}, checking out only takes 1 click.`
          : `Did you know that you can use ${String(pbbName)} at any participating dispensary?`}
      </Typography.Body>

      <StyledTitle size='medium' tag='h3'>
        Linked Bank
      </StyledTitle>

      {bankAccountData && (
        <BankCardContainer>
          <BankWrapper wrapButton={needsRelinkBankAccount} hideBorder>
            <BankInfo>
              <BankInfoInner>
                {financialInstitutionLogo && <BankLogo src={financialInstitutionLogo} />}
                {!financialInstitutionLogo && <BankIcon height={36} width={36} />}
                <BankTextWrapper>
                  <BankName maxWidth={385}>{bankAccountData.bankName}</BankName>
                  <BankDetail>{`${t('common.accountEndingIn', 'ending in')} ${bankAccountData.lastDigits}`}</BankDetail>
                </BankTextWrapper>
              </BankInfoInner>
              {needsRelinkBankAccount && (
                <ErrorWrapper>
                  <InfoIconAlt color={colors.red[55]} />
                  An error has occurred with your bank connection. Please relink to continue.
                </ErrorWrapper>
              )}
            </BankInfo>
            {needsRelinkBankAccount && <RelinkBankButton onClick={handleRelinkClick} label='Relink Bank' />}
            <EditButton data-testid='edit-bank-account' type='button' onClick={handleEditClick}>
              {t('common.edit', 'Edit')}
            </EditButton>
          </BankWrapper>
          {!!isShowLoyaltyPassFlagEnabled && <AddToWallet />}
        </BankCardContainer>
      )}

      {!bankAccountData && (
        <BankWrapper wrapButton>
          <BankInfo>
            <BankInfoInner>
              <BankTextWrapper>
                <BankName>No connected bank</BankName>
                <BankDetail>Your bank was disconnected</BankDetail>
              </BankTextWrapper>
            </BankInfoInner>
            {needsRelinkBankAccount && (
              <ErrorWrapper>
                <InfoIconAlt color={colors.red[55]} />
                {`Add a bank to use ${PBB_NAME}`}
              </ErrorWrapper>
            )}
          </BankInfo>

          <RelinkButton type='button' onClick={handleEditClick}>
            {t('common.add-bank', 'Add Bank')}
          </RelinkButton>
        </BankWrapper>
      )}
    </>
  );
}
